import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { View } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useUIStore, useCollectionStore, useModalStore, useChatStore, useOnboardingStore, FILTER_CONFIGS } from '@/store';
import { useRouter } from 'expo-router';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import useRequireAuth from '@/hooks/useRequireAuth';
import SidebarListContainer from './SidebarListContainer';
import SidebarListItemCollection from './SidebarListItemCollection';
import SidebarListItemPlaceholder from '@/components/Placeholders/SidebarListItemPlaceholder';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import SidebarListItemLayout from '@/components/Sidebar/SidebarListItemLayout';
import { buildFolderTree, sortFolderTree } from '@/constants/utils/folder_tree';

function SidebarCollectionsSection() {
  const { t } = useTranslation();
  const router = useRouter();
  const route = useCurrentRoute();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Collections state
  const collections = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const sortOrder = useCollectionStore(state => state.sortOrder);
  const pagination = useCollectionStore(state => state.pagination);
  const isLoading = useCollectionStore(state => state.isLoading.fetch);
  const handleFilterSortClick = useCollectionStore(state => state.handleFilterSortClick);
  const fetchCollections = useCollectionStore(state => state.fetchCollections);
  const { openModal } = useModalStore(state => state.actions);
  const { registerTarget, unregisterTarget } = useOnboardingStore(state => state.actions);
  const requireAuth = useRequireAuth();
  const sectionRef = useRef(null);

  useEffect(() => {
    if (sectionRef.current) {
        registerTarget(5, sectionRef);
    }
    return () => unregisterTarget(5);
  }, [registerTarget, unregisterTarget]);

  // Get chats from store
  const chats = useChatStore(state => state.chats);

  // UI State
  const collapsedSections = useUIStore(state => state.collapsedSections);
  const toggleSection = useUIStore(state => state.toggleSection);

  // Memoize the isCollectionRoute function to prevent recreation
  const isCollectionRoute = useCallback((id) => route.isCollection(id), [route]);
  const isLibraryChat = useCallback((id) => route.isLibraryChat(id), [route]);

  // Memoize the collection items to prevent unnecessary re-renders
  const displayedItems = useMemo(() => {
    return collectionIds
      .map(id => {
        const item = collections[id];
        if (!item) return null;

        // Apply the Collections sort to chats inside every folder as well.
        const collectionChats = (item.conversationIds || [])
          .map((conversationId) => {
            const chat = chats.find(c => c?.id === conversationId);
            if (!chat) return null;
            return {
              ...chat,
              isActive: isLibraryChat(chat.id)
            };
          })
          .filter(Boolean)
          .sort((a, b) => {
            if (sortOrder === 'a-z') return (a.title || '').localeCompare(b.title || '');
            if (sortOrder === 'z-a') return (b.title || '').localeCompare(a.title || '');
            const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
            const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
            return sortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
          });

        // Determine most recent activity timestamp using reliable fields
        const latestChat = collectionChats.length > 0 
          ? collectionChats.reduce((latest, current) => {
              const currentTime = new Date(current.updatedAt || current.createdAt || 0).getTime();
              const latestTime = new Date(latest.updatedAt || latest.createdAt || 0).getTime();
              return currentTime > latestTime ? current : latest;
            }, collectionChats[0])
          : null;
        const lastActivityMs = latestChat ? new Date(latestChat.updatedAt || latestChat.createdAt || 0).getTime() : 0;

        return {
          id: item.id,
          title: item.title,
          href: `/collections/${item.id}`,
          isArchived: item?.flags?.isArchived,
          isActive: isCollectionRoute(item.id),
          chatCount: collectionChats.length,
          chats: collectionChats,
          lastActivityMs,
          parentId: item.parentId || null,
        };
      })
      .filter(Boolean);
  }, [collections, collectionIds, isCollectionRoute, isLibraryChat, chats, sortOrder]);

  // Sort displayed items based on sort order, then nest by parentId
  const sortedItems = useMemo(() => {
    const compare = (a, b) => {
      switch (sortOrder) {
        case 'newest':
          return (b.lastActivityMs || 0) - (a.lastActivityMs || 0);
        case 'oldest':
          return (a.lastActivityMs || 0) - (b.lastActivityMs || 0);
        case 'a-z':
          return a.title.localeCompare(b.title);
        case 'z-a':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    };
    return sortFolderTree(buildFolderTree(displayedItems), compare);
  }, [displayedItems, sortOrder]);

  const handleCollectionsLabelClick = useCallback((labelId) => {
    if (labelId === null) {
      handleFilterSortClick(null);
      router.push('/files');
    } else {
      handleFilterSortClick(labelId);
    }
  }, [router, handleFilterSortClick]);

  const getCurrentCollectionsPath = useCallback(() => {
    const path = [];
    if (sortOrder) {
      const sortLabel = FILTER_CONFIGS.collections.sort
        .find(l => l.id === sortOrder);
      if (sortLabel) path.push(sortLabel);
    }
    return path;
  }, [sortOrder]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !pagination.hasMore) return;
    
    try {
      setIsLoadingMore(true);
      const nextPage = pagination.page + 1;
      await fetchCollections(nextPage);
    } catch (error) {
      console.error('Error loading more collections:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, pagination, fetchCollections]);

  const handleCreateCollection = useCallback(() => {
    requireAuth(
      () => openModal('CreateCollectionModal'),
      {
        source: 'sidebar_create_collection',
        title: 'Sign in to create a Collection',
        subtitle: 'Collections group files and chats so Gab can pull from the same context across every conversation.',
        icon: 'folder-plus',
      },
    );
  }, [openModal, requireAuth]);

  return (
    <>
      <SidebarListContainer
        containerRef={sectionRef}
        title={t('navigation.collections')}
        labels={[
          { id: 'sort', title: 'Sort', children: FILTER_CONFIGS.collections.sort }
        ]}
        currentLabelPath={getCurrentCollectionsPath()}
        isCollapsed={collapsedSections.collections}
        onToggle={() => toggleSection('collections')}
        onLabelClick={handleCollectionsLabelClick}
        stickyIndexTop={1}
      />
      {!collapsedSections.collections && (
        <>
          <SidebarListItemLayout
            noEllipsis
            title='New collection'
            icon="folder-plus"
            onPress={handleCreateCollection}
          />
          {isLoading ? (
            // Show 4 placeholder items while loading
            Array.from({ length: 4 }).map((_, index) => (
              <SidebarListItemPlaceholder key={`placeholder-${index}`} />
            ))
          ) : (
            <>
              {sortedItems.map((item, index) => (
                <SidebarListItemCollection
                  key={`sidebar-list-item-collection-${item?.id || index}`}
                  id={item.id}
                  title={item.title}
                  href={item.href}
                  isArchived={item.isArchived}
                  isActive={item.isActive}
                  chatCount={item.chatCount}
                  chats={item.chats}
                  childCollections={item.children || []}
                />
              ))}
              {pagination.hasMore && !isLoading && (
                <LoadMoreButton
                  onPress={handleLoadMore}
                  isLoading={isLoadingMore}
                  style={{ marginVertical: 4, marginHorizontal: 5 }}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

export default React.memo(SidebarCollectionsSection);
